/**
 * Subtitle helpers for the per-row ExceptionDrawer.
 *
 * Configs supply ``getRowSubtitle`` to compose a single muted line of
 * context shown under the Item title - e.g. "Walk-in · 12 Mar 2026 ·
 * $456.78" for sales, "SKU ABC-123 · Active · $19.99" for products.
 *
 * The composer below filters out empty / nullish / non-string parts so
 * each config can read raw row fields without per-call guard clauses.
 * Parts are joined with " · " (interpunct) - the same separator used
 * across the inspector for muted metadata strips.
 */
import { formatCurrency, formatBusinessDate } from '../../../utils/inspectorFormatters'

const SEPARATOR = ' · ' // " · "

/**
 * Compose a subtitle string from an ordered list of bits. Each bit
 * may be a non-empty string, a finite number (cast to string), or
 * null / undefined / '' (dropped).
 *
 * Returns ``undefined`` when no usable parts remain so the drawer can
 * suppress the subtitle line entirely instead of rendering an empty
 * caption.
 */
export const composeRowSubtitle = (
  parts: Array<string | number | null | undefined | false>,
): string | undefined => {
  const cleaned: string[] = []
  for (const p of parts) {
    if (p === null || p === undefined || p === false) continue
    if (typeof p === 'number') {
      if (!Number.isFinite(p)) continue
      cleaned.push(String(p))
      continue
    }
    const trimmed = String(p).trim()
    if (trimmed === '') continue
    cleaned.push(trimmed)
  }
  return cleaned.length ? cleaned.join(SEPARATOR) : undefined
}

/** Format an SKU as ``SKU <value>`` if present, else undefined. */
export const skuPart = (sku: unknown): string | undefined => {
  if (typeof sku !== 'string') return undefined
  const trimmed = sku.trim()
  return trimmed ? `SKU ${trimmed}` : undefined
}

/** Active / Inactive caption from a boolean-ish "active" field. */
export const activePart = (active: unknown): string | undefined => {
  if (active === undefined || active === null) return undefined
  if (typeof active === 'boolean') return active ? 'Active' : 'Inactive'
  if (typeof active === 'string') {
    const norm = active.trim().toLowerCase()
    if (norm === 'yes' || norm === 'true' || norm === 'active') return 'Active'
    if (norm === 'no' || norm === 'false' || norm === 'inactive') return 'Inactive'
  }
  return undefined
}

/** Currency formatted with the project's default locale + AUD style. */
export const moneyPart = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === '') return undefined
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return undefined
  return formatCurrency(n)
}

/** Business-date formatted (DD Mmm YYYY) without time-zone shift. */
export const datePart = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || value.trim() === '') return undefined
  const formatted = formatBusinessDate(value)
  return formatted && formatted !== '-' ? formatted : undefined
}

/** Coerce an unknown to a trimmed string or undefined. */
export const textPart = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined
  const s = String(value).trim()
  return s === '' ? undefined : s
}
